import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Alert from '@material-ui/lab/Alert'
import Dialog from 'components/dialog'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import {
  DELAY_OPTIONS,
  MESSAGE_MAX_LENGTH,
  RULE_COPY,
  describeRule,
  formatDelay,
} from './constants'
import useStyles from './styles'

const insertAt = (text, insertion, start, end) =>
  text.slice(0, start) + insertion + text.slice(end)

export default function EditRuleDialog({
  rule,
  template,
  saving,
  error,
  onCancel,
  onSave,
}) {
  const classes = useStyles()
  const titleClasses = useDialogTitleStyles()
  const messageRef = useRef(null)
  const pendingCaret = useRef(null)
  const [delayDays, setDelayDays] = useState(rule.delayDays)
  const [body, setBody] = useState(template.body)
  const trimmed = body.trim()
  const delayOptions = [
    ...DELAY_OPTIONS.filter(days => days <= rule.maxDelayDays),
    ...(DELAY_OPTIONS.includes(rule.delayDays) ? [] : [rule.delayDays]),
  ].sort((a, b) => a - b)

  useEffect(() => {
    const input = messageRef.current
    if (pendingCaret.current == null || !input) return
    input.focus()
    input.setSelectionRange(pendingCaret.current, pendingCaret.current)
    pendingCaret.current = null
  }, [body])

  const insertMergeField = field => {
    const input = messageRef.current
    const start = input?.selectionStart ?? body.length
    const end = input?.selectionEnd ?? body.length
    pendingCaret.current = start + field.length
    setBody(insertAt(body, field, start, end))
  }

  return (
    <Dialog
      isOpen
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      showCloseButton
      disableClose={saving}
      classes={{ paper: titleClasses.dialogPaper }}
    >
      {() => (
        <>
          <DialogTitle
            disableTypography
            classes={{ root: titleClasses.titleRoot }}
          >
            <Typography component="h2" className={titleClasses.title}>
              Edit: {RULE_COPY[rule.trigger].title}
            </Typography>
            <Typography className={titleClasses.bodyText}>
              {describeRule(rule.trigger, delayDays)} You also start from this
              message when you follow up yourself.
            </Typography>
          </DialogTitle>
          <DialogContent className={titleClasses.content}>
            <TextField
              select
              label="Send after"
              value={delayDays}
              onChange={event => setDelayDays(Number(event.target.value))}
              variant="outlined"
              size="small"
              className={classes.field}
              SelectProps={{ 'data-testid': 'edit-rule-delay' }}
            >
              {delayOptions.map(days => (
                <MenuItem key={days} value={days}>
                  {formatDelay(days)}
                  {days === rule.defaultDelayDays ? ' (recommended)' : ''}
                </MenuItem>
              ))}
            </TextField>
            {rule.active && delayDays !== rule.delayDays ? (
              <Typography className={classes.finePrint}>
                This changes who gets the message, so you&apos;ll approve the
                new list next. If you cancel, it keeps the current timing.
              </Typography>
            ) : null}
            <TextField
              label="Message"
              value={body}
              onChange={event => setBody(event.target.value)}
              multiline
              minRows={5}
              fullWidth
              variant="outlined"
              inputRef={messageRef}
              inputProps={{
                maxLength: MESSAGE_MAX_LENGTH,
                'data-testid': 'edit-rule-message',
              }}
              helperText={`${body.length}/${MESSAGE_MAX_LENGTH}${
                rule.active ? '. Changes apply to the next automated send.' : ''
              }`}
            />
            <div className={classes.mergeFields}>
              <Typography component="span" className={classes.mergeFieldsLabel}>
                Insert:
              </Typography>
              {template.mergeFields.map(field => (
                <Chip
                  key={field}
                  size="small"
                  variant="outlined"
                  label={field}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => insertMergeField(field)}
                />
              ))}
            </div>
            {template.optionalMergeFields.length > 0 ? (
              <Typography
                className={classes.finePrint}
                style={{ marginTop: 8 }}
              >
                {template.optionalMergeFields.join(', ')} can be empty, like
                when a student has no private lessons or you have no open times
                in the next two weeks. Keep each on its own line. A line is left
                out when its placeholder is empty.
              </Typography>
            ) : null}
            {error ? (
              <Alert severity="warning" style={{ marginTop: 16 }}>
                {error}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions className={titleClasses.actions}>
            {body !== template.defaultBody ? (
              <Button
                onClick={() => setBody(template.defaultBody)}
                color="primary"
                size="large"
                className={classes.resetButton}
                disabled={saving}
              >
                Use Default Message
              </Button>
            ) : null}
            <Button
              onClick={onCancel}
              color="primary"
              size="large"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => onSave({ delayDays, body: trimmed })}
              color="primary"
              variant="contained"
              size="large"
              disableElevation
              disabled={
                !trimmed || trimmed.length > MESSAGE_MAX_LENGTH || saving
              }
              data-testid="edit-rule-save"
            >
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

EditRuleDialog.propTypes = {
  rule: PropTypes.shape({
    trigger: PropTypes.string.isRequired,
    active: PropTypes.bool,
    delayDays: PropTypes.number.isRequired,
    defaultDelayDays: PropTypes.number.isRequired,
    maxDelayDays: PropTypes.number.isRequired,
  }).isRequired,
  template: PropTypes.shape({
    body: PropTypes.string.isRequired,
    defaultBody: PropTypes.string.isRequired,
    mergeFields: PropTypes.arrayOf(PropTypes.string).isRequired,
    optionalMergeFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
}
